import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { Box, Button, FormControl, OutlinedInput, InputAdornment, IconButton, CircularProgress, Tooltip } from '@mui/material';
import { debounce } from '@mui/material/utils';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import { Lock } from '@mui/icons-material';
import { Colors } from '../styles/colors';

type SearchFieldType = {
  initialValue?: string;
  disabled?: boolean;
  debounceTime?: number;
  placeholder?: string;
  showSearchButton?: boolean;
  isSearching?: boolean;
  tooltipText?: string;
  fullWidth?: boolean;
  onSearch: (searchString: string) => void;
};

export default function SearchField(props: SearchFieldType) {
  const {
    initialValue = '',
    disabled = false,
    debounceTime = 500,
    placeholder: customPlaceholder,
    showSearchButton = false,
    isSearching = false,
    tooltipText,
    fullWidth: fullWidthProp = false,
    onSearch,
  } = props;
  const [focused, setFocused] = useState<boolean>(false);
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState<boolean>(false);
  const [value, setValue] = useState<string>(initialValue || '');
  const [isUserInput, setIsUserInput] = useState<boolean>(false);

  const doSearch = (val) => {
    setLoading(false);
    setIsUserInput(false);
    if (val.length >= 2) onSearch(val);
  };

  const debouncedSearch = debounce(doSearch, debounceTime);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    if (!showSearchButton) {
      setIsUserInput(true);
      if (event.target.value.length >= 2) setLoading(true);
      else {
        setLoading(false);
        onSearch('');
      }
    }
  };

  useEffect(() => {
    if (!showSearchButton && isUserInput) {
      debouncedSearch(value);
    }
    return () => {
      debouncedSearch.clear();
    };
  }, [value, debouncedSearch, isUserInput, showSearchButton]);

  const handleSearchNow = () => {
    debouncedSearch.clear();
    setLoading(false);
    setIsUserInput(false);
    if (value.length >= 2) {
      onSearch(value);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchNow();
    }
  };

  const handleClearValue = () => {
    setValue('');
    onSearch('');
    setIsUserInput(false);
  };

  let endAdornment: React.ReactNode;
  if (loading) {
    endAdornment = <CircularProgress size={20} />;
  } else if (disabled) {
    endAdornment = <Lock sx={{ color: 'text.disabled' }} />;
  } else {
    endAdornment = (
      <IconButton aria-label='reset search' onClick={handleClearValue} edge='end' size='small' sx={{ visibility: value ? 'visible' : 'hidden' }}>
        <ClearIcon />
      </IconButton>
    );
  }

  const input = (
    <FormControl
      variant='outlined'
      size='small'
      fullWidth={fullWidthProp}
      sx={{
        '& .MuiInputBase-root': {
          backgroundColor: disabled ? Colors.Form.DisabledInput : '#fff',
        },
        '& .MuiInputBase-input::placeholder': {
          color: '#666',
          opacity: 1,
        },
        '& .MuiInputAdornment-root': {
          fontSize: '1rem',
          color: 'grey.400',
        },
      }}
      disabled={disabled}
    >
      <OutlinedInput
        id='outlined-adornment-password'
        type='text'
        autoComplete='off'
        value={value}
        placeholder={customPlaceholder || t('common:button.search')}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        startAdornment={
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position='end' sx={{ width: 5, justifyContent: 'flex-end' }}>
            {endAdornment}
          </InputAdornment>
        }
      />
    </FormControl>
  );

  const formControl = tooltipText ? (
    <Tooltip title={tooltipText} open={focused} arrow>
      {input}
    </Tooltip>
  ) : (
    input
  );

  if (showSearchButton) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{formControl}</Box>
        <Button
          variant='contained'
          size='small'
          onClick={isSearching ? undefined : handleSearchNow}
          disabled={disabled || value.length < 2}
          sx={{ height: '40px', pointerEvents: isSearching ? 'none' : 'auto' }}
        >
          {isSearching ? <CircularProgress size={18} color='inherit' /> : t('common:button.search')}
        </Button>
      </Box>
    );
  }

  return formControl;
}
